import { BigNumber } from '@ethersproject/bignumber'
import { getAddress } from '@ethersproject/address'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { Contract } from '@ethersproject/contracts'
import { AddressZero } from '../constants/constants'
import IRouterV1 from '../abis/IRouter.json'
import IRouterV2 from '../abis/IBrownFiV2Router.json'
import IRouterWithPrice from '../abis/IRouterWithPrice.json'
import {
  CurrencyAmount,
  getRouterAddress,
  JSBI,
  Token,
  TokenAmount
} from '../sdk'
import { ROUTER_ADDRESS_WITH_PRICE } from '../sdk/constants'

/**
 * Returns true if the string value is zero in hex
 * @param hexNumberString
 */
export default function isZero(hexNumberString: string) {
  return /^0x0*$/.test(hexNumberString)
}

export function calculateGasMargin(value: BigNumber): BigNumber {
  return value
    .mul(BigNumber.from(10000).add(BigNumber.from(1000)))
    .div(BigNumber.from(10000))
}

export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

// account is not optional
export function getSigner(
  library: Web3Provider,
  account: string
): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

export function getProviderOrSigner(
  library: Web3Provider,
  account?: string
): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

export function getContract(
  address: string,
  ABI: any,
  library: Web3Provider,
  account?: string
): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(
    address,
    ABI,
    getProviderOrSigner(library, account) as any
  )
}

export function getRouterContract(
  chainId: number,
  library: Web3Provider,
  account: string,
  version: number
): Contract {
  const IRouter = version === 2 ? IRouterV2 : IRouterV1
  return getContract(
    getRouterAddress(chainId, version),
    IRouter,
    library,
    account
  )
}

export function getRouterContractWithPrice(
  chainId: number,
  library: Web3Provider,
  account?: string
): Contract {
  return getContract(
    ROUTER_ADDRESS_WITH_PRICE[chainId],
    IRouterWithPrice,
    library,
    account
  )
}

export function calculateSlippageAmount(
  value: CurrencyAmount,
  slippage: number
): [JSBI, JSBI] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  return [
    JSBI.divide(
      JSBI.multiply(value.raw, JSBI.BigInt(10000 - slippage)),
      JSBI.BigInt(10000)
    ),
    JSBI.divide(
      JSBI.multiply(value.raw, JSBI.BigInt(10000 + slippage)),
      JSBI.BigInt(10000)
    )
  ]
}

export function applySlippageNumber(value: BigNumber, slippage: number) {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  const numerator = BigNumber.from(10000 + slippage) // e.g., 10100 for 1% slippage
  const denominator = BigNumber.from(10000) // 100% = 10000 basis points

  return value.mul(numerator).div(denominator)
}

export function applySlippageCurrency(
  value: CurrencyAmount,
  slippage: number
): CurrencyAmount {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  const amount = parseFloat(value.toExact()) * (1 + slippage / 10000)

  return new TokenAmount(
    value.currency as Token,
    JSBI.BigInt(Math.round(amount * 10 ** value.currency.decimals))
  )
}
